import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'
import { WorkspaceActionBarWidget } from './WorkspaceActionBar'
import WorkspaceActionBarWidgetLabel from './WorkspaceActionBarWidgetLabel'

const transitionDuration = '100ms'
const useWidgetContainerStyles = makeStyles((theme: Theme) => ({
    widgetGroupWrapper: {
        display: 'inline-flex',
    },
    widgetGroupContainer: {
        display: 'inline-flex',
        width: 'fit-content',
        transition: transitionDuration,
        overflow: 'hidden',
        '&:not(:last-child)': {
            borderRight: '1px solid rgba(255, 255, 255, 0.25)',
        },
    },
    widgetContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(0, 2),
        marginTop: theme.spacing(0.25),
        transition: transitionDuration,
        '@media (max-width: 1024px)': {
            padding: theme.spacing(0, 1),
        },
    },
    activeWidgetContainer: { height: '97%' },
    widgetComponentContainer: {
        margin: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    hasBorder: {
        borderLeft: '1px solid rgba(255, 255, 255, 0.25)',
    },
    groupBorder: {
        borderRight: '1px solid rgba(255, 255, 255, 0.25)',
    },
    collapsedWidgetGroupContainer: {
        height: 30,
    },
    expandedWidgetGroupContainer: {
        height: 70,
    },
}))

export interface WorkspaceActionBarWidgetContainerProps {
    /**
     * JSON configuration for the widgets/widgt groups.
     */
    widgets?: WorkspaceActionBarWidget[][]
    /**
     * Determines whether the widgets should reflect collapsed
     * or expanded state.
     */
    isCollapsed?: boolean

    /**
     * Shows/hides divider on the leftmost end
     */
    showLeftBorder?: boolean
    showGroupBorder?: boolean
}

export interface WidgetLabelStylesProps {
    textAlign: 'left' | 'right' | 'center'
}

/**
 * This is a container for widget
 * groups. It is responsible for generating the
 * widgets/widget groups passed in as props.
 */
const WorkspaceActionBarWidgetContainer: React.FC<WorkspaceActionBarWidgetContainerProps> = ({
    widgets = [],
    isCollapsed,
    showLeftBorder = false,
    showGroupBorder = false,
}) => {
    const classes = useWidgetContainerStyles()

    return (
        <div
            className={clsx(
                classes.widgetGroupWrapper,
                showLeftBorder && classes.hasBorder,
                showGroupBorder && classes.groupBorder
            )}
        >
            {widgets.map(
                (
                    widgetGroup: WorkspaceActionBarWidget[],
                    widgetGroupIndex: number
                ) => (
                    <div
                        key={`widgetGroup-${widgetGroupIndex}`}
                        className={clsx(
                            classes.widgetGroupContainer,
                            isCollapsed
                                ? classes.collapsedWidgetGroupContainer
                                : classes.expandedWidgetGroupContainer
                        )}
                    >
                        {widgetGroup.map(
                            (
                                widgetItem: WorkspaceActionBarWidget,
                                widgetIndex: number
                            ) => (
                                <div
                                    className={clsx(
                                        widgetItem.customContainerStyle ??
                                            classes.widgetContainer
                                    )}
                                    key={`widget-${widgetIndex}`}
                                >
                                    {widgetItem.label && (
                                        <WorkspaceActionBarWidgetLabel
                                            isCollapsed={isCollapsed}
                                            label={widgetItem.label}
                                        />
                                    )}
                                    <div
                                        className={
                                            classes.widgetComponentContainer
                                        }
                                    >
                                        {widgetItem.component}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                )
            )}
        </div>
    )
}

export default WorkspaceActionBarWidgetContainer
